import { Service } from '@/services/api';
import {
  AIModel,
  AIPrompt,
  CreatePromptRequest,
  UpdatePromptRequest,
  UpdatePromptResponse,
  DeletePromptResponse,
  InvokeModelRequest,
  InvokeModelResponse,
  AIPromptResult,
  CreatePromptResultRequest,
  CreatePromptResultResponse,
  AIPromptMapping,
  AIPromptMappingRequest,
  AIPromptMappingUpdateRequest,
  PublishPromptVersionResponse,
  InvokeModelAsyncResponse,
  ModelInvocationStatusResponse,
  ModelInvocation,
  FieldOverrideListResponse,
  CreateFieldOverrideRequest,
  MergedPromptResultResponse,
  AIPromptResultReview,
  AIPromptResultWithReviews,
  CreateReviewResponse,
  ReviewPreviewResponse,
} from '@/types/ai';
import {
  PromptField,
  CreatePromptFieldRequest,
  UpdatePromptFieldRequest,
} from '@/types/PromptField';
import { Document } from '@/types/ui/PromptManager';

class AIService extends Service {
  async getPrompt(id: number, version: string): Promise<AIPrompt> {
    return await this.get<AIPrompt>(`/v2/ai/prompts/${id}?version=${version}`).then(this.handleResponse);
  }

  async getAllModels(): Promise<AIModel[]> {
    return await this.get<AIModel[]>('/v2/ai/models').then(this.handleResponse);
  }

  async getAllPrompts(): Promise<AIPrompt[]> {
    return await this.get<AIPrompt[]>('/v2/ai/prompts/versions/all').then(this.handleResponse);
  }

  async createPrompt(request: CreatePromptRequest): Promise<{ promptId: number }> {
    return await this.post<{promptId: number}>('/v2/ai/prompts', request).then(this.handleResponse);
  }

  async updatePrompt(id: number, request: UpdatePromptRequest): Promise<UpdatePromptResponse> {
    return await this.put<UpdatePromptResponse>(`/v2/ai/prompts/${id}`, request).then(this.handleResponse);
  }

  async updatePromptDraftOnly(id: number, request: UpdatePromptRequest): Promise<UpdatePromptResponse> {
    return await this.patch<UpdatePromptResponse>(`/v2/ai/prompts/${id}`, request).then(this.handleResponse);
  }

  async publishPromptVersion(id: number): Promise<PublishPromptVersionResponse> {
    return await this.post<PublishPromptVersionResponse>(`/v2/ai/prompts/${id}/publish`, {}).then(this.handleResponse);
  }

  async deletePrompt(id: number, version: number): Promise<DeletePromptResponse> {
    return await this.delete<DeletePromptResponse>(`/v2/ai/prompts/${id}/versions/${version}`).then(this.handleResponse);
  }

  async deleteAllVersionsOfPrompt(id: number): Promise<DeletePromptResponse> {
    return await this.delete<DeletePromptResponse>(`/v2/ai/prompts/${id}`).then(this.handleResponse);
  }

  async invokeModel(request: InvokeModelRequest): Promise<InvokeModelResponse> {
    return await this.post<InvokeModelResponse>(`/v2/ai/invoke-model`, {
      ...request,
      payload: JSON.stringify(request.payload),
    }).then(this.handleResponse);
  }

  async invokeModelAsync(request: InvokeModelRequest): Promise<InvokeModelAsyncResponse> {
    return await this.post<InvokeModelAsyncResponse>(`/v2/ai/invoke-model-async`, {
      modelId: request.modelId,
      payload: JSON.stringify(request.payload),
      s3Key: request.s3Key,
      documentId: request.documentId,
      expectedOutputSchema: request.expectedOutputSchema,
      promptId: request.promptId,
      promptVersion: request.promptVersion,
    }).then(this.handleResponse);
  }

  async getInvocationStatus(invocationId: string): Promise<ModelInvocationStatusResponse> {
    return await this.get<ModelInvocationStatusResponse>(`/v2/ai/invocation/${invocationId}/status`).then(this.handleResponse);
  }

  async getInvocationResult(invocationId: string): Promise<AIPromptResult> {
    return await this.get<AIPromptResult>(`/v2/ai/invocation/${invocationId}/result`).then(this.handleResponse);
  }

  async getActiveInvocations(limit?: number): Promise<ModelInvocation[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = `/v2/ai/invocations/active${queryString ? `?${queryString}` : ''}`;

    return await this.get<ModelInvocation[]>(url).then(this.handleResponse);
  }

  async getAllDocuments(): Promise<Document[]> {
    return await this.get<Document[]>('/v2/ai/documents').then(this.handleResponse);
  }

  async uploadDocument(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    return await this.post<string>(`/v2/ai/documents`, formData).then(this.handleInsertResponse);
  }

  async deleteDocument(s3Key: string): Promise<void> {
    await this.delete(`/v2/ai/documents/${encodeURIComponent(s3Key)}`).then(this.handleResponse);
  }

  async downloadDocument(s3Key: string, convertToPdf: boolean = false): Promise<string> {
    return await this.get(`/v2/ai/documents/${encodeURIComponent(s3Key)}/download`, { convertToPdf }, {
      responseType: 'blob',
    }).then(this.handleFile);
  }

  async createPromptResult(request: CreatePromptResultRequest): Promise<CreatePromptResultResponse> {
    return await this.post<CreatePromptResultResponse>('/v2/ai/prompt-results', request).then(this.handleResponse);
  }

  async getPromptResults(limit?: number, offset?: number): Promise<AIPromptResult[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const queryString = params.toString();
    const url = `/v2/ai/prompt-results${queryString ? `?${queryString}` : ''}`;

    return await this.get<AIPromptResult[]>(url).then(this.handleResponse);
  }

  async getPromptMappings(): Promise<AIPromptMapping[]> {
    return await this.get<AIPromptMapping[]>('/v2/ai/prompt-mappings').then(this.handleResponse);
  }

  async createPromptMapping(mapping: AIPromptMappingRequest): Promise<AIPromptMapping> {
    return await this.post<AIPromptMapping>('/v2/ai/prompt-mappings', { ...mapping }).then(this.handleResponse);
  }

  async updatePromptMapping(id: number, request: AIPromptMappingUpdateRequest): Promise<void> {
    await this.put(`/v2/ai/prompt-mappings/${id}`, request).then(this.handleResponse);
  }

  async deletePromptMapping(id: string): Promise<void> {
    await this.delete(`/v2/ai/prompt-mappings/${id}`).then(this.handleResponse);
  }

  // Prompt Field Library methods
  async getAllPromptFields(category?: string): Promise<PromptField[]> {
    const url = category ? `/v2/ai/prompt-fields?category=${encodeURIComponent(category)}` : '/v2/ai/prompt-fields';
    return await this.get<PromptField[]>(url).then(this.handleResponse);
  }

  async getPromptField(id: number): Promise<PromptField> {
    return await this.get<PromptField>(`/v2/ai/prompt-fields/${id}`).then(this.handleResponse);
  }

  async createPromptField(request: CreatePromptFieldRequest): Promise<PromptField> {
    return await this.post<PromptField>('/v2/ai/prompt-fields', request).then(this.handleResponse);
  }

  async updatePromptField(id: number, request: UpdatePromptFieldRequest): Promise<PromptField> {
    return await this.put<PromptField>(`/v2/ai/prompt-fields/${id}`, request).then(this.handleResponse);
  }

  async deletePromptField(id: number): Promise<void> {
    await this.delete(`/v2/ai/prompt-fields/${id}`).then(this.handleResponse);
  }

  // Field Override methods (CAC-5113: AI Field Override Review Mode)
  async getFieldOverrides(promptResultId: number): Promise<FieldOverrideListResponse> {
    return await this.get<FieldOverrideListResponse>(
      `/v2/ai/prompt-results/${promptResultId}/field-overrides`
    ).then(this.handleResponse);
  }

  async createFieldOverride(promptResultId: number, request: CreateFieldOverrideRequest): Promise<void> {
    await this.post(`/v2/ai/prompt-results/${promptResultId}/field-overrides`, request).then(
      this.handleResponse
    );
  }

  async deleteFieldOverride(promptResultId: number, fieldPath: string): Promise<void> {
    await this.delete(
      `/v2/ai/prompt-results/${promptResultId}/field-overrides/${encodeURIComponent(fieldPath)}`
    ).then(this.handleResponse);
  }

  async getMergedPromptResult(promptResultId: number): Promise<MergedPromptResultResponse> {
    return await this.get<MergedPromptResultResponse>(
      `/v2/ai/prompt-results/${promptResultId}/merged`
    ).then(this.handleResponse);
  }

  async getReviewPreview(promptResultId: number): Promise<ReviewPreviewResponse> {
    return await this.get<ReviewPreviewResponse>(
      `/v2/ai/prompt-results/${promptResultId}/review/preview`
    ).then(this.handleResponse);
  }

  // Review methods (CAC-5113: Track who reviewed extraction results)
  async markAsReviewed(promptResultId: number): Promise<CreateReviewResponse> {
    return await this.post<CreateReviewResponse>(
      `/v2/ai/prompt-results/${promptResultId}/review`,
      {}
    ).then(this.handleResponse);
  }

  async getReviews(promptResultId: number): Promise<AIPromptResultReview[]> {
    return await this.get<AIPromptResultReview[]>(
      `/v2/ai/prompt-results/${promptResultId}/reviews`
    ).then(this.handleResponse);
  }

  async getPromptResultsWithReviews(limit?: number, offset?: number): Promise<AIPromptResultWithReviews[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    const queryString = params.toString();
    const url = `/v2/ai/prompt-results-with-reviews${queryString ? `?${queryString}` : ''}`;
    return await this.get<AIPromptResultWithReviews[]>(url).then(this.handleResponse);
  }
}

export const aiService = new AIService();
